import React, { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { VarChips } from "@/components/LeadMessaging";
import { Plus, Trash2, Pencil, Mail, MessageSquare } from "lucide-react";
import { toast } from "sonner";

const inputCls = "w-full border border-[#E6E4DD] rounded-sm px-3 text-sm focus:outline-none focus:border-forest";

function TemplateEditor({ channel, initial, onClose, onSaved }) {
  const [form, setForm] = useState(initial || { name: "", subject: "", body: "" });
  const save = async () => {
    try {
      if (form.id) await api.patch(`/message-templates/${form.id}`, { name: form.name, subject: form.subject, body: form.body });
      else await api.post("/message-templates", { ...form, channel });
      toast.success("Template saved"); onSaved();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="rounded-sm max-w-lg">
        <DialogHeader><DialogTitle className="font-display text-2xl">{form.id ? "Edit" : "New"} {channel === "email" ? "email" : "SMS"} template</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <input data-testid="tpl-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Template name (e.g. Site visit follow-up)" className={`${inputCls} h-10`} />
          {channel === "email" && <input data-testid="tpl-subject" value={form.subject || ""} onChange={(e) => setForm({ ...form, subject: e.target.value })} placeholder="Subject" className={`${inputCls} h-10`} />}
          <textarea data-testid="tpl-body" value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })} rows={channel === "email" ? 8 : 4} placeholder="Hi {first_name}, thanks for your interest in {project}…" className={`${inputCls} py-2`} />
          <VarChips onPick={(v) => setForm((f) => ({ ...f, body: f.body + v }))} />
        </div>
        <DialogFooter>
          <button data-testid="tpl-save" onClick={save} disabled={!form.name.trim() || !form.body.trim() || (channel === "email" && !(form.subject || "").trim())} className="h-9 px-4 rounded-sm bg-forest text-white text-sm font-medium disabled:opacity-50">Save template</button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function MessageTemplates() {
  const { user } = useAuth();
  const canEdit = ["admin", "manager"].includes(user?.role);
  const [channel, setChannel] = useState("email");
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const load = async () => setItems((await api.get("/message-templates", { params: { channel } })).data);
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [channel]);
  const remove = async (t) => {
    if (!window.confirm(`Delete "${t.name}"?`)) return;
    await api.delete(`/message-templates/${t.id}`); load();
  };
  const tab = (c, Icon, label) => (
    <button data-testid={`tpl-tab-${c}`} onClick={() => setChannel(c)} className={`px-3 h-9 text-xs uppercase tracking-[0.15em] font-bold inline-flex items-center gap-1.5 transition-colors duration-150 ${channel === c ? "bg-forest text-white" : "text-forest/70 hover:text-forest"}`}><Icon className="h-3.5 w-3.5" /> {label}</button>
  );
  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <div className="label-caps">Console</div>
          <h2 className="font-display font-black text-3xl text-forest tracking-tight mt-1">Email & SMS templates</h2>
          <div className="text-sm text-forest/60 mt-1">Saved messages your team can send from any lead. Fields like {"{name}"} fill in automatically.</div>
        </div>
        <div className="flex items-center gap-2">
          <div className="inline-flex border border-[#E6E4DD] rounded-sm bg-white">{tab("email", Mail, "Email")}{tab("sms", MessageSquare, "SMS")}</div>
          {canEdit && <button data-testid="tpl-new" onClick={() => setEditing({})} className="h-9 px-4 rounded-sm bg-forest text-white text-sm font-medium inline-flex items-center gap-2"><Plus className="h-4 w-4" /> New template</button>}
        </div>
      </div>
      <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-4" data-testid="tpl-list">
        {items.map((t) => (
          <div key={t.id} data-testid={`tpl-card-${t.id}`} className="border border-[#E6E4DD] bg-white rounded-sm p-5 space-y-2">
            <div className="flex items-start justify-between gap-2">
              <div className="font-display font-bold text-forest">{t.name}</div>
              {canEdit && (
                <div className="flex gap-1">
                  <button data-testid={`tpl-edit-${t.id}`} onClick={() => setEditing(t)} className="p-1.5 text-forest/60 hover:text-forest"><Pencil className="h-3.5 w-3.5" /></button>
                  <button data-testid={`tpl-delete-${t.id}`} onClick={() => remove(t)} className="p-1.5 text-forest/60 hover:text-clay"><Trash2 className="h-3.5 w-3.5" /></button>
                </div>
              )}
            </div>
            {t.subject && <div className="text-xs text-forest/70">Subject: {t.subject}</div>}
            <div className="text-sm text-forest/70 whitespace-pre-line line-clamp-5">{t.body}</div>
          </div>
        ))}
        {items.length === 0 && <div className="md:col-span-3 border border-dashed border-[#E6E4DD] rounded-sm p-12 text-center text-sm text-forest/50">No {channel === "email" ? "email" : "SMS"} templates yet.</div>}
      </div>
      {editing && <TemplateEditor channel={channel} initial={editing.id ? editing : null} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}
    </div>
  );
}
